import { useContext, useEffect, useRef, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/button';
import ButtonIcon from 'components/buttonIcon';
import TeamSelect from '../TeamSelect';
import Switch from 'components/switch/Switch';
import { Team, UserTeam } from 'contexts/teams/types';
import Notification from 'components/notification';
import { SessionContext } from 'contexts/session';
import { isTeamScopedManager } from 'contexts/session/utilities';
import useLicenseAvailability from 'hooks/useLicenseAvailability';
import { getInviteLink, getUpdatedInviteLink } from 'services/inviteLink';
import { Container, InviteLinkInput } from './legacyStyles';
import { INE_URL } from 'constants/domains';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import { INVITE_LINK_NOT_CREATED, INVITE_LINK_NOT_UPDATED, LINK_COPIED } from 'constants/invites';
import { UserTeamsPayload } from 'components/usersTable/types';
import { colors } from 'constants/styles/colors';

// The link tab as it was before the Skill Dive invite flow, shown while that flow's flag is off.
// Delete this file and legacyStyles.tsx once the flag is retired.

interface InviteByLink {
  assign_license: boolean;
  teams: UserTeamsPayload[];
}

const AddByLinkLegacy: React.FC<{ handleBackPress?: () => void }> = ({ handleBackPress }) => {
  const [inviteData, setInviteData] = useState<InviteByLink>(null);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userTeams, setUserTeams] = useState<UserTeam[]>([]);
  const [managerSelected, setManagerSelected] = useState<boolean>(false);
  const creating = useRef(false);
  const { licenses_available, teams, isTeamManager, isAccountOwner, isStaff } =
    useContext(SessionContext);
  const needsTeam = isTeamScopedManager({ isTeamManager, isAccountOwner, isStaff });

  const licenseAvailability = useLicenseAvailability({ count: licenses_available });
  const infiniteLicenses = licenseAvailability.mode === 'unlimited';
  const hasLicensesLeft = infiniteLicenses || (licenseAvailability.value ?? 0) > 0;

  const resolveAssignLicense = () => {
    if (typeof inviteData?.assign_license === 'boolean') return inviteData.assign_license;
    if (typeof invite?.assign_license === 'boolean') return invite.assign_license;
    return hasLicensesLeft;
  };

  const resolveTeams = () => {
    if (Array.isArray(inviteData?.teams)) return inviteData.teams;
    if (Array.isArray(invite?.teams)) return invite.teams;
    if (userTeams.length > 0) {
      return userTeams.reduce<UserTeamsPayload[]>((acc, userTeam) => {
        const teamId = userTeam.team?.id || userTeam.team_id;
        if (!teamId) return acc;
        acc.push({ team_id: teamId, is_manager: managerSelected });
        return acc;
      }, []);
    }
    if (isTeamManager && teams?.length === 1 && teams[0]?.id) {
      return [{ team_id: teams[0].id, is_manager: managerSelected }];
    }
    return [];
  };

  const updateInviteData = async () => {
    try {
      const linkResponse = await getUpdatedInviteLink({
        assign_license: resolveAssignLicense(),
        teams: resolveTeams(),
        inviteId: invite?.id,
      });
      if (linkResponse.status >= 200 && linkResponse.status < 300) {
        setInvite(linkResponse.data);
      }
    } catch (error) {
      Notification({ text: INVITE_LINK_NOT_UPDATED, type: NOTIFICATION_TYPES.ERROR });
    } finally {
      setLoading(false);
    }
  };

  const getInviteData = async () => {
    if (creating.current) return;
    const payload = {
      assign_license: resolveAssignLicense(),
      teams: resolveTeams(),
    };
    if (needsTeam && payload.teams.length === 0) {
      setLoading(false);
      return;
    }
    creating.current = true;
    setLoading(true);
    try {
      const linkResponse = await getInviteLink(payload);
      if (linkResponse.status === 201) {
        setInvite(linkResponse.data);
      }
    } catch (error) {
      Notification({ text: INVITE_LINK_NOT_CREATED, type: NOTIFICATION_TYPES.ERROR });
    } finally {
      creating.current = false;
      setLoading(false);
    }
  };

  const handleSettingsChange = ({ type, value }) => {
    setInviteData((prevState) => {
      return {
        ...prevState,
        [type]: value,
      };
    });
  };

  const handleCopyLink = () => {
    const urlElement = document.createElement('input');
    document.body.appendChild(urlElement);
    urlElement.value = `${INE_URL}/invite/${invite.id}`;
    urlElement.select();
    document.execCommand('copy');
    document.body.removeChild(urlElement);
    Notification({ text: LINK_COPIED, type: NOTIFICATION_TYPES.SUCCESS });
  };

  const handleUpdateLicenses = (checked: boolean) => {
    handleSettingsChange({ type: 'assign_license', value: checked });
  };

  const handleTeamChange = (newTeams: Team[]) => {
    setUserTeams(
      newTeams.reduce((acc, team) => {
        acc.push({ team: team, is_manager: managerSelected });
        return acc;
      }, [])
    );
  };

  const handleManagerChange = (checked: boolean) => {
    setManagerSelected(checked);
    const updatedTeams = userTeams.reduce((acc, userTeam) => {
      acc.push({ team_id: userTeam.team.id, is_manager: checked });
      return acc;
    }, []);
    handleSettingsChange({ type: 'teams', value: updatedTeams });
  };

  useEffect(() => {
    setLoading(true);
    getInviteData();
  }, []);

  useEffect(() => {
    if (!inviteData) return;
    if (!invite?.id) {
      getInviteData();
      return;
    }
    if (needsTeam && resolveTeams().length === 0) return;
    updateInviteData();
  }, [inviteData]);

  useEffect(() => {
    if (!invite?.id || !inviteData) return;
    if (needsTeam && resolveTeams().length === 0) return;
    updateInviteData();
  }, [invite?.id]);

  useEffect(() => {
    if (inviteData?.teams?.length > 0 || userTeams.length > 0) {
      const updatedTeams = userTeams.reduce((acc, userTeam) => {
        acc.push({ team_id: userTeam.team.id, is_manager: managerSelected });
        return acc;
      }, []);
      updatedTeams.length === 0 && setManagerSelected(false);
      handleSettingsChange({ type: 'teams', value: updatedTeams });
    }
  }, [userTeams]);

  useEffect(() => {
    if (!isTeamManager || teams?.length !== 1) return;
    if (userTeams.length > 0) return;
    const defaultTeam = teams[0];
    setUserTeams([{ team: defaultTeam, is_manager: false }]);
  }, [isTeamManager, teams, userTeams.length]);

  const waitingForTeam = needsTeam && resolveTeams().length === 0;
  const copyDisabled = loading || !invite?.id || waitingForTeam;

  return (
    <>
      {isMobile && (
        <FeatherIcon
          icon={'arrow-left'}
          style={{ position: 'absolute', top: 20, left: 10 }}
          color={colors.fonts.white}
          onClick={handleBackPress}
        />
      )}
      <Container>
        <div className={'invite-link-container'}>
          <InviteLinkInput>
            {waitingForTeam
              ? !loading && 'Select a team to get the invite link'
              : invite?.id && `${INE_URL}/invite/${invite.id}`}
            {loading && (
              <FeatherIcon icon="loader" color={colors.palette.Business_DataVizLilacLighter} />
            )}
          </InviteLinkInput>
          <Switch
            label={'License'}
            defaultChecked={hasLicensesLeft}
            style={{ marginLeft: '12px' }}
            disabled={!hasLicensesLeft}
            onChange={(checked: boolean) => handleUpdateLicenses(checked)}
          />
          <ButtonIcon icon={'Copy'} onClick={handleCopyLink} disabled={copyDisabled} />
        </div>
        <div className={'invite-link-settigs'}>
          <div className={'invite-link-teams'}>
            <TeamSelect
              teamsSelected={userTeams}
              onTeamChange={handleTeamChange}
              withManagerSwitch={true}
              onSwitchChange={handleManagerChange}
              isManager={managerSelected}
            />
          </div>
        </div>
        {isDesktop && (
          <span className={'licenses-available'}>
            {' '}
            Licenses available: <b>{infiniteLicenses ? '∞' : licenseAvailability.value}</b>
          </span>
        )}
        <Button onClick={handleCopyLink} disabled={copyDisabled}>
          <FeatherIcon icon="copy" color={colors.palette.Business_DataVizLilacLighter} /> Copy
          invite link
        </Button>
      </Container>
    </>
  );
};

export default AddByLinkLegacy;
